import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function isoToDmy(iso) {
  if (!iso || !iso.includes("-")) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export default function RefettoriPage() {
  const [tab, setTab] = useState("giorno"); // "giorno" | "config"
  const [dateIso, setDateIso] = useState(todayIso());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Config tab state
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [replace, setReplace] = useState(false);
  const fileRef = useRef(null);

  const caricaGiorno = async (iso) => {
    const dmy = isoToDmy(iso);
    if (!dmy) return;
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/mensa/refettori/giorno", { params: { data: dmy } });
      setReport(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento refettori del giorno");
      setReport(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { caricaGiorno(dateIso); /* eslint-disable-next-line */ }, []);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true); setErr(null); setImportReport(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/mensa/refettori/import-xlsx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { replace: replace ? "true" : "false" },
      });
      setImportReport(r.data);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Errore durante l'importazione");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Svuotare la collection di import legacy?")) return;
    try {
      await api.delete("/mensa/refettori");
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  const maxClassi = useMemo(() => {
    if (!report?.refettori?.length) return 1;
    return Math.max(1, ...report.refettori.map(r => r.n_classi || 0));
  }, [report]);

  return (
    <div>
      <div style={{ margin: "8px 20px 0", display: "flex", gap: 2, borderBottom: "2px solid #8e24aa" }}>
        <TabBtn active={tab === "giorno"} onClick={() => setTab("giorno")} testid="ref-tab-giorno">
          📅 Refettori del giorno
        </TabBtn>
        <TabBtn active={tab === "config"} onClick={() => setTab("config")} testid="ref-tab-config">
          🍽 Refettori tutti
        </TabBtn>
      </div>

      {err && <div style={{ margin: "8px 20px", padding: 10, background: "#fff0f0", border: "1px solid #fcc", color: "#c00", fontSize: 12 }} data-testid="ref-error">{err}</div>}

      {tab === "giorno" && (
        <GiornoView
          dateIso={dateIso} setDateIso={(iso) => { setDateIso(iso); caricaGiorno(iso); }}
          report={report} loading={loading} maxClassi={maxClassi}
          onReload={() => caricaGiorno(dateIso)}
        />
      )}

      {tab === "config" && (
        <ConfigView
          importing={importing} importReport={importReport} setImportReport={setImportReport}
          replace={replace} setReplace={setReplace}
          fileRef={fileRef} handleFile={handleFile} handleClear={handleClear}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      style={{ padding: "8px 18px", border: "none", background: active ? "#8e24aa" : "white", color: active ? "white" : "#8e24aa",
        fontWeight: 700, fontSize: 12, cursor: "pointer", borderRadius: "4px 4px 0 0", borderTop: "1px solid #ccc", borderLeft: "1px solid #ccc", borderRight: "1px solid #ccc" }}>
      {children}
    </button>
  );
}

function GiornoView({ dateIso, setDateIso, report, loading, maxClassi, onReload }) {
  const gg = report?.gruppo_giorno;
  const ggLabel = gg === 1 ? "1 · Lun/Mer/Ven" : gg === 2 ? "2 · Mar/Gio" : "—";
  const [editing, setEditing] = useState(null); // {codice, idx, value}
  const [plessoClassi, setPlessoClassi] = useState([]);
  useEffect(() => {
    (async () => {
      try { const r = await api.get("/mensa/plesso-classi"); setPlessoClassi(r.data.items || []); }
      catch (_e) { /* ignore */ }
    })();
  }, []);

  const saveClasse = async (r, idx, newValue) => {
    const oldValue = r.classi[idx]?.plesso_classe || "";
    const v = (newValue || "").trim().toUpperCase();
    if (v === oldValue.trim().toUpperCase()) { setEditing(null); return; }
    const newClassi = r.classi.map(c => c?.plesso_classe || "");
    if (v) newClassi[idx] = v; else newClassi.splice(idx, 1);
    try {
      await api.put(`/mensa/refettori-config/${r.codice}`, {
        codice: r.codice,
        nome: r.refettorio,
        gruppo_giorno: gg,
        ordinamento: r.ordinamento,
        gruppo_stampa: r.gruppo_stampa || "",
        ordine_stampa: r.ordine_stampa || 0,
        classi: newClassi.filter(Boolean),
        valido_dal: r.valido_dal || "",
      });
      setEditing(null);
      onReload && onReload();
    } catch (_e) {
      alert("Errore salvataggio classe");
      setEditing(null);
    }
  };

  return (
    <>
      <div style={{ margin: "8px 20px", background: "white", border: "1px solid #ccc", padding: 12, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#666", marginBottom: 3 }}>DATA</div>
          <input type="date" data-testid="refg-date" value={dateIso} onChange={e => setDateIso(e.target.value)}
            style={{ padding: "5px 10px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>
        {dateIso && report && (
          <button onClick={() => {
              const d = new Date(dateIso);
              const dmy = `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
              const url = `${api.defaults.baseURL}/mensa/refettori/giorno/pdf?data=${encodeURIComponent(dmy)}`;
              window.open(url, "_blank");
            }}
            data-testid="refg-pdf"
            style={{ padding: "6px 14px", background: "#c62828", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            🖨 Stampa report
          </button>
        )}
        {report && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#4a148c" }}>
              📆 {report.giorno} · Gruppo giorno: <b>{ggLabel}</b>
            </div>
            {!report.has_presenze && (
              <div style={{ fontSize: 11, color: "#c62828", fontStyle: "italic" }}>
                ⚠ Nessuna presenza mensa importata per questa data — i conteggi alunni/intolleranti sono 0.
              </div>
            )}
          </>
        )}
        {report && report.gruppo_giorno != null && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
            <StatBox label="REFETTORI" value={report.n_refettori} color="#8e24aa" />
            <StatBox label="ALUNNI" value={report.tot_alunni} color="#1565c0" />
            <StatBox label="INTOLLERANTI" value={report.tot_intolleranti} color="#c62828" />
            {report.tot_non_assegnati > 0 && <StatBox label="NON ASSEGN." value={report.tot_non_assegnati} color="#b71c1c" />}
          </div>
        )}
      </div>

      {report?.tot_non_assegnati > 0 && (
        <div data-testid="refg-non-assegnati" style={{ margin: "0 20px 12px", background: "#fff5f5", border: "2px solid #ef5350", borderRadius: 6, padding: "10px 14px" }}>
          <div style={{ fontWeight: 800, color: "#b71c1c", fontSize: 13, marginBottom: 6 }}>
            ⚠ {report.tot_non_assegnati} alunni NON assegnati ad alcun refettorio — verifica la configurazione (Refettori tutti)
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {report.non_assegnati.map((na, i) => (
              <span key={i} data-testid={`refg-na-${i}`} style={{ background: "white", border: "1px solid #ef9a9a", borderRadius: 12, padding: "3px 10px", fontSize: 12, color: "#b71c1c", fontWeight: 700 }}>
                {na.plesso} · {na.classe}: <b>{na.alunni}</b>{na.intolleranti > 0 ? ` (⚠${na.intolleranti})` : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      <div style={{ margin: "0 20px 20px", background: "white", border: "1px solid #ccc" }}>
        <div style={{ maxHeight: "68vh", overflow: "auto" }}>
          <table className="sw-table sw-table-compact" style={{ fontSize: 11 }}>
            <thead style={{ position: "sticky", top: 0, background: "#29b6f6", color: "#01579b", zIndex: 2 }}>
              <tr>
                <th style={{ width: 40, textAlign: "center" }}>ORD.</th>
                <th style={{ minWidth: 200 }}>REFETTORIO</th>
                {Array.from({ length: maxClassi }, (_, i) => (
                  <th key={`c-${i}`} style={{ minWidth: 150, textAlign: "left", background: "#e1f5fe", color: "#01579b" }}>CLASSE {i + 1}</th>
                ))}
                <th style={{ width: 70, textAlign: "right", background: "#fff9c4", color: "#000" }}>ALUNNI</th>
                <th style={{ width: 90, textAlign: "right", background: "#ffcdd2", color: "#b71c1c" }}>INTOLL.</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={2 + maxClassi + 2} style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>}
              {!loading && !report && <tr><td colSpan={2 + maxClassi + 2} style={{ padding: 30, textAlign: "center", color: "#888" }}>Seleziona una data.</td></tr>}
              {!loading && report && report.gruppo_giorno == null && (
                <tr><td colSpan={2 + maxClassi + 2} style={{ padding: 30, textAlign: "center", color: "#888", fontStyle: "italic" }}>
                  {report.message}
                </td></tr>
              )}
              {!loading && report?.gruppo_giorno != null && report.refettori.length === 0 && (
                <tr><td colSpan={2 + maxClassi + 2} style={{ padding: 30, textAlign: "center", color: "#888" }}>
                  Nessuna configurazione refettori per il gruppo giorno {report.gruppo_giorno}. Vai su 🍽 Refettori tutti per importare il file.
                </td></tr>
              )}
              {!loading && report?.refettori?.map((r, i) => (
                <tr key={r.refettorio} data-testid={`refg-row-${i}`}>
                  <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 700, color: "#01579b" }}>{r.ordinamento ?? ""}</td>
                  <td style={{ fontWeight: 700, color: "#8e24aa", fontSize: 13, background: "#f3e5f5" }}>{r.refettorio}</td>
                  {Array.from({ length: maxClassi }, (_, idx) => {
                    const c = r.classi[idx];
                    const isEditing = editing?.codice === r.codice && editing?.idx === idx;
                    if (isEditing) {
                      return (
                        <td key={idx} style={{ background: "#fffde7", padding: 3 }}>
                          <input autoFocus list="ref-classi-opts" defaultValue={c?.plesso_classe || ""}
                            data-testid={`refg-edit-${r.codice}-${idx}`}
                            onBlur={(e) => saveClasse(r, idx, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") e.target.blur();
                              if (e.key === "Escape") setEditing(null);
                            }}
                            style={{ width: "100%", fontSize: 10, padding: "4px 6px", border: "2px solid #8e24aa", fontFamily: "Consolas, monospace" }} />
                        </td>
                      );
                    }
                    if (!c) return (
                      <td key={idx} onClick={() => setEditing({ codice: r.codice, idx, value: "" })}
                        title="Clicca per aggiungere una classe"
                        style={{ background: "#fafafa", cursor: "pointer" }}>
                        <span style={{ color: "#ccc", fontSize: 10 }}>+ aggiungi</span>
                      </td>
                    );
                    const isVirt = !!c.virtuale;
                    return (
                      <td key={idx} onClick={() => setEditing({ codice: r.codice, idx, value: c.plesso_classe })}
                        title="Clicca per modificare · Canc per rimuovere"
                        style={{ background: isVirt ? "#fff3e0" : "#f5f5f5", fontSize: 10, lineHeight: 1.3, borderLeft: isVirt ? "3px solid #ef6c00" : undefined, cursor: "pointer" }}>
                        <div style={{ fontWeight: 700, color: isVirt ? "#ef6c00" : "#263238" }} title={isVirt ? c.label : ""}>
                          {isVirt && "✨ "}{c.plesso_classe}
                        </div>
                        <div style={{ color: "#666" }}>
                          <span title="Alunni in classe presenti a mensa">👥 {c.alunni}</span>
                          {c.intolleranti > 0 && <span style={{ marginLeft: 6, color: "#c62828" }} title="Intolleranti">⚠ {c.intolleranti}</span>}
                        </div>
                      </td>
                    );
                  })}
                  <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 800, fontSize: 14, background: "#fff9c4" }}>{r.tot_alunni}</td>
                  <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 700, color: r.tot_intolleranti > 0 ? "#b71c1c" : "#888", background: r.tot_intolleranti > 0 ? "#ffebee" : "transparent" }}>{r.tot_intolleranti}</td>
                </tr>
              ))}
            </tbody>
            {report?.refettori?.length > 0 && (
              <tfoot>
                <tr style={{ background: "#263238", color: "white", fontWeight: 700 }}>
                  <td colSpan={2 + maxClassi} style={{ textAlign: "right", padding: "6px 8px" }}>TOTALE</td>
                  <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", fontSize: 14 }}>{report.tot_alunni}</td>
                  <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", fontSize: 14 }}>{report.tot_intolleranti}</td>
                </tr>
              </tfoot>
            )}
          </table>
          <datalist id="ref-classi-opts">
            {plessoClassi.map(p => <option key={p} value={p} />)}
          </datalist>
        </div>
      </div>
    </>
  );
}

function ConfigView({ importing, importReport, setImportReport, replace, setReplace, fileRef, handleFile, handleClear }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [plessoClassi, setPlessoClassi] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [versions, setVersions] = useState([]);
  const [selVersion, setSelVersion] = useState(""); // "" = versione base/attuale; altrimenti "YYYY-MM-DD"
  const [matriceVer, setMatriceVer] = useState(null);   // versione usata dalla Matrice Mensa
  const [matriceFonte, setMatriceFonte] = useState(null);
  const [attivaLoaded, setAttivaLoaded] = useState(false); // 🆕 versione attiva persistente caricata

  const fmtVersione = (v) => {
    if (!v) return "Attuale (base)";
    const [y, m, d] = v.split("-");
    return `Dal ${d}/${m}/${y}`;
  };

  const carica = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/mensa/refettori-config", { params: { valido_dal: selVersion } });
      setItems(r.data.items || []);
      setVersions(r.data.versions || [""]);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };

  const caricaClassi = async () => {
    try {
      // Sorgenti: plesso-classi (include VIRTUALI) + refettori-distinti (generati dall'elaborazione)
      const [r1, r2] = await Promise.all([
        api.get("/mensa/plesso-classi"),
        api.get("/mensa/refettori-distinti").catch(() => ({ data: { items: [] } })),
      ]);
      const set = new Set([...(r1.data.items || []), ...(r2.data.items || [])]);
      setPlessoClassi(Array.from(set).sort());
    } catch { /* ignore */ }
  };

  useEffect(() => { if (attivaLoaded) { carica(); caricaClassi(); } /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selVersion, attivaLoaded]);

  // 🆕 Al primo ingresso: carica la versione ATTIVA persistente e selezionala (non resettare alla prima della lista)
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/mensa/refettori-config");
        if (r.data?.versione_attiva_impostata) setSelVersion(r.data.versione_attiva || "");
      } catch { /* ignore */ }
      setAttivaLoaded(true);
    })();
  }, []);

  // 🆕 Cambio versione dal menu = imposta la versione ATTIVA (persistente + usata da report/Matrice)
  const cambiaVersione = async (v) => {
    setSelVersion(v);
    try {
      await api.post("/mensa/refettori-config/set-attiva", { valido_dal: v });
      setMatriceVer(v);
      setMatriceFonte("config");
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio versione attiva");
    }
  };

  // Versione refettori realmente usata dalla Matrice Mensa (endpoint /mensa-matrice/matrice)
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/mensa-matrice/matrice");
        setMatriceVer(r.data?.refettori_versione ?? "");
        setMatriceFonte(r.data?.refettori_fonte ?? "config");
      } catch { /* ignore */ }
    })();
  }, []);

  const nuovo = () => {
    setEditRow({ codice: "", nome: "", gruppo_giorno: 1, classi: [""], ordinamento: (items.length + 1), gruppo_stampa: "", ordine_stampa: 0, _isNew: true });
  };

  const nuovaVersione = async () => {
    const def = `${new Date().getFullYear()}-09-01`;
    const vd = window.prompt("Nuova versione refettori valida DAL giorno (formato AAAA-MM-GG).\nVerranno copiati i refettori della versione attualmente selezionata; poi potrai modificare le classi.", def);
    if (!vd) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(vd.trim())) { setErr("Data non valida. Usa il formato AAAA-MM-GG (es. 2026-09-01)."); return; }
    try {
      const r = await api.post("/mensa/refettori-config/nuova-versione", { valido_dal: vd.trim(), from_valido_dal: selVersion || "" });
      alert(`✓ Creata versione "Dal ${vd}" con ${r.data.created} refettori copiati.\nOra selezionala e modifica le classi.`);
      setSelVersion(vd.trim());
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore creazione versione");
    }
  };

  const modifica = (r) => {
    const classi = [...(r.classi || [])];
    if (classi.length < 1) classi.push("");
    setEditRow({ ...r, classi, _orig_codice: r.codice });
  };

  const salva = async () => {
    if (!editRow) return;
    setSaving(true); setErr(null);
    try {
      const payload = {
        codice: (editRow.codice || "").trim().toUpperCase(),
        nome: (editRow.nome || "").trim(),
        gruppo_giorno: editRow.gruppo_giorno,
        classi: editRow.classi.filter(c => c && c.trim()),
        ordinamento: editRow.ordinamento,
        gruppo_stampa: (editRow.gruppo_stampa || "").trim(),
        ordine_stampa: parseInt(editRow.ordine_stampa || 0, 10) || 0,
        valido_dal: selVersion || "",
      };
      if (editRow._isNew) {
        await api.post("/mensa/refettori-config", payload);
      } else {
        await api.put(`/mensa/refettori-config/${editRow._orig_codice}`, payload);
      }
      setEditRow(null);
      await carica();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const elimina = async (r) => {
    if (!window.confirm(`Eliminare il refettorio "${r.nome}" (${r.codice})?`)) return;
    try {
      await api.delete(`/mensa/refettori-config/${r.codice}`, { params: { valido_dal: selVersion || "" } });
      await carica();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  const migra = async () => {
    if (!window.confirm("Popolare la configurazione dai dati già importati da Excel? Verranno aggregati per refettorio + gruppo giorno.")) return;
    setMigrating(true);
    try {
      const r = await api.post("/mensa/refettori-config/migrate-from-xlsx-import");
      alert(`Migrazione completata: inseriti ${r.data.inserted}, aggiornati ${r.data.updated} (${r.data.total_groups} refettori totali).`);
      await carica();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore migrazione");
    } finally { setMigrating(false); }
  };

  const salvaClasseInline = async (r, idx, newValue) => {
    const v = (newValue || "").trim().toUpperCase();
    const oldValue = (r.classi?.[idx] || "").trim().toUpperCase();
    if (v === oldValue) return;
    const newClassi = [...(r.classi || [])];
    if (v) {
      if (idx < newClassi.length) newClassi[idx] = v;
      else newClassi.push(v);
    } else {
      if (idx < newClassi.length) newClassi.splice(idx, 1);
    }
    try {
      await api.put(`/mensa/refettori-config/${r.codice}`, {
        codice: r.codice, nome: r.nome, gruppo_giorno: r.gruppo_giorno,
        ordinamento: r.ordinamento, gruppo_stampa: r.gruppo_stampa, ordine_stampa: r.ordine_stampa,
        classi: newClassi.filter(Boolean), valido_dal: selVersion || "",
      });
      await carica();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio classe");
    }
  };

  const salvaCampoInline = async (r, field, value) => {
    const newVal = field === "ordine_stampa" ? (parseInt(value || 0, 10) || 0) : (value || "").trim();
    if (newVal === (r[field] || (field === "ordine_stampa" ? 0 : ""))) return;
    try {
      await api.put(`/mensa/refettori-config/${r.codice}`, {
        codice: r.codice, nome: r.nome, gruppo_giorno: r.gruppo_giorno,
        ordinamento: r.ordinamento, classi: r.classi || [],
        gruppo_stampa: field === "gruppo_stampa" ? newVal : (r.gruppo_stampa || ""),
        ordine_stampa: field === "ordine_stampa" ? newVal : (r.ordine_stampa || 0),
        valido_dal: selVersion || "",
      });
      await carica();
    } catch (e) { setErr(e.response?.data?.detail || "Errore salvataggio"); }
  };

  const jsonImportRef = useRef(null);
  const exportJson = async () => {
    try {
      const r = await api.get("/mensa/refettori-config/export-json");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `refettori_cfg_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(e.response?.data?.detail || "Errore export"); }
  };
  const importJson = async (file) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const items = data.items || data;
      if (!Array.isArray(items)) { setErr("File non valido"); return; }
      if (!window.confirm(`Importare ${items.length} refettori? I record con stesso codice verranno aggiornati.`)) return;
      const r = await api.post("/mensa/refettori-config/import-json", { items, mode: "upsert" });
      alert(`✓ Import completato\nInseriti: ${r.data.inserted}\nAggiornati: ${r.data.updated}\nErrori: ${r.data.errors?.length || 0}`);
      await carica();
    } catch (e) { setErr(e.response?.data?.detail || e.message || "Errore import"); }
    finally { if (jsonImportRef.current) jsonImportRef.current.value = ""; }
  };

  const [filterGruppo, setFilterGruppo] = useState("");
  const itemsFiltered = useMemo(() => {
    if (!filterGruppo) return items;
    const gg = Number(filterGruppo);
    return items.filter(r => Number(r.gruppo_giorno) === gg);
  }, [items, filterGruppo]);

  return (
    <>
      <div style={{ margin: "8px 20px", background: "white", border: "1px solid #ccc", padding: 12, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <button onClick={nuovo} data-testid="refc-new"
          style={{ padding: "6px 14px", background: "#8e24aa", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          + Nuovo refettorio
        </button>
        <span style={{ borderLeft: "1px solid #ddd", paddingLeft: 10, display: "inline-flex", alignItems: "center", gap: 6 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: "#5e35b1" }} title="Versione ATTIVA della lista refettori: è quella usata da Refettori del giorno, report e Matrice Mensa">✅ Versione attiva:</label>
          <select value={selVersion} onChange={e => cambiaVersione(e.target.value)} data-testid="refc-version"
            title="La versione selezionata qui viene salvata come ATTIVA e usata per la ripartizione classi/gruppi nei refettori. Resta impostata anche uscendo e rientrando."
            style={{ padding: "5px 10px", border: "2px solid #2e7d32", fontSize: 12, fontWeight: 700, color: "#1b5e20", background: "#e8f5e9" }}>
            {(versions.length ? versions : [""]).map(v => <option key={v || "base"} value={v}>{fmtVersione(v)}</option>)}
          </select>
          <button onClick={nuovaVersione} data-testid="refc-new-version"
            title="Crea una nuova versione della lista refettori valida da una data (es. nuovo anno scolastico), copiando la versione selezionata"
            style={{ padding: "5px 10px", background: "white", color: "#5e35b1", border: "1px solid #5e35b1", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            ➕ Nuova versione (da data)
          </button>
        </span>
        {matriceVer !== null && (
          <span data-testid="refc-matrice-version"
            title={matriceFonte === "tabella_import"
              ? "La Matrice Mensa sta usando la tabella di import legacy (nessuna config strutturata presente)"
              : "Versione della lista refettori usata da report e Matrice Mensa per dividere gli alunni per refettorio (= versione attiva impostata)"}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", background: "#ede7f6", border: "1px solid #7e57c2", borderRadius: 4, fontSize: 11, fontWeight: 700, color: "#4527a0" }}>
            🍽 Report/Matrice usa: {matriceFonte === "tabella_import" ? "Tabella import (legacy)" : fmtVersione(matriceVer)}
          </span>
        )}
        {items.length > 0 && (
          <button onClick={handleClear} data-testid="ref-clear"
            style={{ padding: "6px 10px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            🗑 Svuota import legacy
          </button>
        )}
        <select value={filterGruppo} onChange={e => setFilterGruppo(e.target.value)}
          data-testid="refc-filter-gruppo" title="Filtra per gruppo giorno"
          style={{ padding: "5px 10px", border: "1px solid #1565c0", fontSize: 12, fontWeight: 700, color: "#1565c0" }}>
          <option value="">Tutti i gruppi</option>
          <option value="1">Gruppo 1 · Lun/Mer/Ven</option>
          <option value="2">Gruppo 2 · Mar/Gio</option>
        </select>
        <div style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, color: "#4a148c" }}>
          {filterGruppo ? `${itemsFiltered.length} / ${items.length}` : items.length} refettori configurati
        </div>
      </div>

      {err && <div style={{ margin: "0 20px 8px", padding: 10, background: "#fff0f0", border: "1px solid #fcc", color: "#c00", fontSize: 12 }}>{err}</div>}
      {importReport && (
        <div style={{ margin: "0 20px 8px", padding: 10, background: "#f3e5f5", border: "1px solid #ce93d8", color: "#4a148c", fontSize: 12 }}>
          <b>Import legacy:</b> inseriti <b>{importReport.inserted}</b>, aggiornati <b>{importReport.updated}</b> · righe totali: <b>{importReport.total_after}</b>. Ora clicca <b>🔄 Popola da import Excel</b> per convertire in config strutturata.
          <button onClick={() => setImportReport(null)} style={{ marginLeft: 10, background: "none", border: "none", color: "#4a148c", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>✕</button>
        </div>
      )}

      <div style={{ margin: "0 20px 20px", background: "white", border: "1px solid #ccc" }}>
        <div style={{ maxHeight: "70vh", overflow: "auto" }}>
          <table className="sw-table sw-table-compact" style={{ fontSize: 11, width: "100%", tableLayout: "auto" }}>
            <thead style={{ position: "sticky", top: 0, background: "#1f3864", color: "white", zIndex: 2 }}>
              <tr>
                <th style={{ width: 90, textAlign: "left", paddingLeft: 8 }}>CODICE</th>
                <th style={{ minWidth: 160 }}>NOME REFETTORIO</th>
                <th style={{ width: 70, textAlign: "center" }}>GRP GIORNO</th>
                <th style={{ width: 55, textAlign: "center" }}>ORD.</th>
                <th style={{ width: 80, textAlign: "center" }} title="Gruppo di stampa (etichetta libera)">GRP STAMPA</th>
                <th style={{ width: 60, textAlign: "center" }} title="Ordine all'interno del gruppo di stampa">ORD. ST.</th>
                <th style={{ textAlign: "center", background: "#0d47a1" }}>CLASSI / GRUPPI COLLEGATI</th>
                <th style={{ width: 80, textAlign: "center" }}>AZIONI</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>}
              {!loading && itemsFiltered.length === 0 && items.length > 0 && !editRow && (
                <tr><td colSpan={8} style={{ padding: 20, textAlign: "center", color: "#888" }}>
                  Nessun refettorio per il filtro selezionato.
                </td></tr>
              )}
              {!loading && items.length === 0 && !editRow && (
                <tr><td colSpan={8} style={{ padding: 30, textAlign: "center", color: "#888" }}>
                  Nessun refettorio configurato. Clicca <b>+ Nuovo refettorio</b>.
                </td></tr>
              )}
              {!loading && itemsFiltered.map((r, i) => (
                <tr key={r.codice} data-testid={`refc-row-${i}`}>
                  <td style={{ textAlign: "left", paddingLeft: 8, fontFamily: "Consolas, monospace", fontWeight: 700, color: "#0d47a1" }}>{r.codice}</td>
                  <td style={{ fontWeight: 700, color: "#8e24aa" }}>{r.nome}</td>
                  <td style={{ textAlign: "center", fontWeight: 700 }}>{r.gruppo_giorno}</td>
                  <td style={{ textAlign: "center", fontFamily: "Consolas, monospace" }}>{r.ordinamento}</td>
                  <td style={{ textAlign: "center", padding: 2 }}>
                    <input defaultValue={r.gruppo_stampa || ""}
                      data-testid={`refc-grpst-${i}`}
                      onBlur={(e) => salvaCampoInline(r, "gruppo_stampa", e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      style={{ width: 70, fontSize: 11, padding: "3px 5px", border: "1px solid #e0e0e0", textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 700, background: "#fffde7", color: "#5d4037" }} />
                  </td>
                  <td style={{ textAlign: "center", padding: 2 }}>
                    <input type="number" defaultValue={r.ordine_stampa ?? 0}
                      data-testid={`refc-ordst-${i}`}
                      onBlur={(e) => salvaCampoInline(r, "ordine_stampa", e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      style={{ width: 50, fontSize: 11, padding: "3px 5px", border: "1px solid #e0e0e0", textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 700, background: "#fffde7", color: "#5d4037" }} />
                  </td>
                  {(() => {
                    const cls = r.classi || [];
                    const slots = [...cls, ""]; // input vuoto finale per aggiungere
                    return (
                      <td style={{ padding: 4 }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {slots.map((val, idx) => (
                            <input
                              key={idx}
                              defaultValue={val}
                              list="refc-classi-opts"
                              placeholder={val ? "" : "+ classe/gruppo"}
                              data-testid={`refc-cls-inline-${i}-${idx}`}
                              onBlur={(e) => salvaClasseInline(r, idx, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") e.target.blur();
                                if (e.key === "Escape") { e.target.value = val; e.target.blur(); }
                              }}
                              title="Clicca per modificare. Svuota + Invio per rimuovere."
                              style={{
                                width: val ? "auto" : 110, minWidth: val ? 90 : 110, flex: "0 1 auto",
                                fontSize: 10, padding: "3px 6px", borderRadius: 3,
                                border: val ? "1px solid #ce93d8" : "1px dashed #bbb",
                                fontFamily: "Consolas, monospace", fontWeight: 600,
                                background: val ? "#f3e5f5" : "#fafafa", color: "#4a148c",
                              }}
                              onFocus={(e) => { e.target.style.border = "2px solid #8e24aa"; e.target.style.background = "#fffde7"; }}
                              onBlurCapture={(e) => { if (val) { e.target.style.border = "1px solid #ce93d8"; e.target.style.background = "#f3e5f5"; } }}
                            />
                          ))}
                        </div>
                      </td>
                    );
                  })()}
                  <td style={{ textAlign: "center" }}>
                    <button onClick={() => modifica(r)} data-testid={`refc-edit-${i}`} title="Modifica tutto"
                      style={{ background: "#1565c0", color: "white", border: "none", padding: "3px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 4 }}>✏</button>
                    <button onClick={() => elimina(r)} data-testid={`refc-del-${i}`} title="Elimina"
                      style={{ background: "#c62828", color: "white", border: "none", padding: "3px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <datalist id="refc-classi-opts">
            {plessoClassi.map(p => <option key={p} value={p} />)}
          </datalist>
        </div>
      </div>

      {editRow && (
        <EditModal row={editRow} setRow={setEditRow} onSave={salva} saving={saving}
          plessoClassi={plessoClassi} onClose={() => setEditRow(null)} />
      )}
    </>
  );
}

function EditModal({ row, setRow, onSave, saving, plessoClassi, onClose }) {
  const update = (patch) => setRow(r => ({ ...r, ...patch }));
  const setClasse = (idx, val) => {
    const classi = [...row.classi];
    classi[idx] = val;
    update({ classi });
  };
  const addClasse = () => {
    if (row.classi.length >= 10) return;
    update({ classi: [...row.classi, ""] });
  };
  const removeClasse = (idx) => {
    const classi = row.classi.filter((_, i) => i !== idx);
    update({ classi: classi.length ? classi : [""] });
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} data-testid="refc-edit-modal"
        style={{ background: "white", width: "100%", maxWidth: 820, borderRadius: 4, overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#8e24aa", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>
            {row._isNew ? "➕ Nuovo Refettorio" : `✏ Modifica Refettorio ${row._orig_codice}`}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 18, overflowY: "auto", flex: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 120px 100px", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>CODICE *</label>
              <input value={row.codice || ""} onChange={e => update({ codice: e.target.value.toUpperCase() })}
                data-testid="refc-edit-codice"
                style={{ width: "100%", padding: 6, border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace", fontWeight: 700 }} />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>NOME REFETTORIO *</label>
              <input value={row.nome || ""} onChange={e => update({ nome: e.target.value })}
                data-testid="refc-edit-nome"
                style={{ width: "100%", padding: 6, border: "1px solid #ccc", fontSize: 12 }} />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>GRP GIORNO *</label>
              <select value={row.gruppo_giorno || 1} onChange={e => update({ gruppo_giorno: Number(e.target.value) })}
                data-testid="refc-edit-gg"
                style={{ width: "100%", padding: 6, border: "1px solid #ccc", fontSize: 12 }}>
                <option value={1}>1 (Lun/Mer/Ven)</option>
                <option value={2}>2 (Mar/Gio)</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>ORD.</label>
              <input type="number" value={row.ordinamento ?? 0} onChange={e => update({ ordinamento: Number(e.target.value) })}
                data-testid="refc-edit-ord"
                style={{ width: "100%", padding: 6, border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace" }} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 100px", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>GRUPPO STAMPA</label>
              <input value={row.gruppo_stampa || ""} onChange={e => update({ gruppo_stampa: e.target.value })}
                data-testid="refc-edit-grpst" placeholder="Etichetta libera (es. PRIMARIA, INFANZIA, ...)"
                style={{ width: "100%", padding: 6, border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace", fontWeight: 700, background: "#fffde7" }} />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>ORD. STAMPA</label>
              <input type="number" value={row.ordine_stampa ?? 0} onChange={e => update({ ordine_stampa: Number(e.target.value) })}
                data-testid="refc-edit-ordst"
                style={{ width: "100%", padding: 6, border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace", background: "#fffde7" }} />
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#666", textTransform: "uppercase" }}>
              Classi collegate ({row.classi.filter(c => c).length} / 10)
            </div>
            <button onClick={addClasse} disabled={row.classi.length >= 10} data-testid="refc-edit-add-classe"
              style={{ padding: "3px 10px", background: row.classi.length >= 10 ? "#e0e0e0" : "#2e7d32", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: row.classi.length >= 10 ? "not-allowed" : "pointer" }}>
              + Aggiungi classe
            </button>
          </div>
          {row.classi.map((val, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
              <div style={{ width: 28, textAlign: "center", fontSize: 11, fontWeight: 700, color: "#8e24aa" }}>{i + 1}</div>
              <input list="pc-options" value={val || ""} onChange={e => setClasse(i, e.target.value)}
                data-testid={`refc-edit-classe-${i}`}
                placeholder="es. BS1E00100D - 1 A"
                style={{ flex: 1, padding: 6, border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace" }} />
              <button onClick={() => removeClasse(i)} title="Rimuovi"
                style={{ background: "none", border: "1px solid #ccc", padding: "4px 8px", cursor: "pointer", fontSize: 11 }}>✕</button>
            </div>
          ))}
          <datalist id="pc-options">
            {plessoClassi.map(p => <option key={p} value={p} />)}
          </datalist>
          <div style={{ fontSize: 10, color: "#888", marginTop: 6, fontStyle: "italic" }}>
            Suggerimento: inizia a digitare il codice plesso (es. BS1E...) per vedere i suggerimenti dalle presenze già importate.
          </div>
          <div style={{ marginTop: 6, padding: 8, background: "#fff8e1", border: "1px solid #ffe082", fontSize: 10, color: "#6d4c41" }}>
            <b style={{ color: "#ef6c00" }}>✨ Classi virtuali</b>: raggruppano gli alunni per <i>motivo di frequenza</i> del giorno (non per classe anagrafica).
            <b> PRIMARIA EXTRA</b> = alunni primaria (E/F) iscritti a un'attività extra quel giorno; <b>SECONDARIA EXTRA</b> = alunni secondaria (M) all'extra; <b>PRIMARIA SPOT</b> = alunni primaria (E/F) con mensa SPOT quel giorno; <b>SECONDARIA SPOT</b> = alunni secondaria (M) con mensa SPOT. Il conteggio è calcolato automaticamente dalle presenze + matrice.
          </div>
        </div>
        <div style={{ padding: "10px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "6px 14px", background: "white", color: "#666", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Annulla</button>
          <button onClick={onSave} disabled={saving} data-testid="refc-edit-save"
            style={{ padding: "6px 18px", background: "#8e24aa", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer" }}>
            {saving ? "Salvataggio..." : "💾 Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div style={{ padding: "6px 14px", background: "white", border: `2px solid ${color}`, borderLeft: `6px solid ${color}`, minWidth: 90 }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color, fontFamily: "Consolas, monospace" }}>{value}</div>
    </div>
  );
}
